<script>
  import { onMount, tick } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import ParticipantDetails from '$lib/ParticipantDetails.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import Modal from './studyForms/FormModal.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import {
    faRotate,
    faEye,
    faCalendarDay,
    faPlus,
    faTimes,
    faTrashCan,
    faPencil,
    faEllipsis,
    faHistory,
    faBug,
  } from '@fortawesome/pro-light-svg-icons';
  import MultiSectionForm from '$lib/MultiSectionForm.svelte';
  import RegularForm from '$lib/RegularForm.svelte';
  // Using local API endpoints instead of direct API access
  const API_BASE_URL = '/api/proxy';
  const demoMode = import.meta.env.VITE_DEMO_MODE === 'true';

  import { formatToLocalDateTime } from '$utils/dateUtils.js';
  import { exportToExcel, generateExportFilename } from '$utils/export.js';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import StudyDetails from '$lib/StudyDetails.svelte';
  import SiteDetails from '$lib/SiteDetails.svelte';
  import AuditLogModal from '$lib/AuditLogModal.svelte';
  import { userRoles, isReadOnly, userReadOnly } from '$stores/sessionStore.js';
  import { hasAuditLogAccess, hasStudyFormAction, hasAccessUiAction } from '$utils/permissionUtils.js';
  import { STUDY_FORM_ACTION_KEYS } from '$lib/permissions/studyFormActions.js';
  import { studyProperties } from '$stores/configStore.js';

  // Get date priority setting from study properties (default to 'formDate')
  $: formDatePriority = $studyProperties?.reports?.reportSettings?.diaryEntryDatePriority || 'formDate';

  let forms = [];
  let resultCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let limit = 20;
  let isLoading = true;
  let searchText = '';
  let selectedDate = new Date().toLocaleDateString('en-CA'); // Format: YYYY-MM-DD (using en-CA locale)
  let dayRange = 14; // days
  let selectedType = 'ALL';
  let selectedStatus = 'ALL';
  let isExporting = false;
  let availableFormTypes = [];

  let isModalOpen = false;
  let modalData = [];
  let modalTitle = 'Title';
  let modalFormDefinition = null;
  let modalDates = null;
  let modalFormTranslations = []; // Translations from original Form
  let modalParticipantLanguageGUID = null; // Participant's language GUID

  // Add form selection modal state
  let showAddFormModal = false;
  let formActivities = [];
  let isLoadingFormActivities = false;
  let publishedSchedule = null;

  // Form filling modal state
  let showFormFillingModal = false;
  let selectedFormDefinition = null;
  let selectedFormJSON = null; // The translated form JSON to use for rendering
  let showEditDebugModal = false;
  let editDebugPayload = '';
  let editDebugFormJSON = '';
  let editDebugPropertiesJSON = '';
  let formTranslationsForFilling = []; // Available translations for language dropdown
  let selectedFillingLanguageGUID = null; // Currently selected language in form filling modal
  let isLoadingFormDefinition = false;
  let formData = {};
  let formErrors = {};
  let addableFieldGroups = {}; // Track addable field groups
  let showDeleteConfirmation = false;
  let formToDelete = null;
  let isEditing = false;
  let editingSubjectFormId = null; // Store the SubjectForm ID when editing
  let editingSubjectForm = null; // Store the SubjectForm document when editing

  // Section navigation state
  let currentSectionIndex = 0;
  let formSections = [];
  let isMultiSectionForm = false;
  let isFormTypeDetermined = false; // Add loading state to prevent form flash

  // Actions menu state
  let showMenu = false;
  let menuForm = null;
  let menuPosition = { top: 0, left: 0, direction: 'down' };
  let menuAnchorRect = null;
  let menuRef = null;

  // Audit log state
  let showAuditLog = false;
  let auditLogForm = null;

  // Computed properties for permissions
  $: isDiaryEditingEnabled = $studyProperties?.features?.diaryEditing === true;
  $: canAddForms =
    isDiaryEditingEnabled &&
    hasStudyFormAction(STUDY_FORM_ACTION_KEYS.ADD, $userRoles, $studyProperties, $userReadOnly);
  $: canEditForms =
    isDiaryEditingEnabled &&
    hasStudyFormAction(STUDY_FORM_ACTION_KEYS.EDIT, $userRoles, $studyProperties, $userReadOnly);
  $: canDeleteForms = hasAccessUiAction(
    'studyForms.delete',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canViewAuditLogs = hasAuditLogAccess($userRoles, $studyProperties, $userReadOnly);
  $: canViewEditDebug = hasAccessUiAction(
    'studyForms.viewDebugJson',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );

  function closeDeleteConfirmationModal() {
    showDeleteConfirmation = false;
    formToDelete = null;
  }

  function handleMenuClick(form, event) {
    event.stopPropagation();
    menuForm = form;
    // Get button position
    const buttonRect = event.currentTarget.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    let direction = 'down';
    let top = buttonRect.bottom;
    let left = buttonRect.left;
    menuAnchorRect = buttonRect;
    menuPosition = { top, left, direction };
    showMenu = true;
    tick().then(() => {
      if (!menuRef || !menuAnchorRect) return;
      const menuHeight = menuRef.offsetHeight || 0;
      const menuWidth = menuRef.offsetWidth || 0;
      let nextDirection = 'down';
      let nextTop = menuAnchorRect.bottom;
      let nextLeft = menuAnchorRect.left;
      if (menuAnchorRect.bottom + menuHeight > viewportHeight) {
        nextDirection = 'up';
        nextTop = menuAnchorRect.top - menuHeight;
      }
      if (nextTop < 0) nextTop = 0;
      if (nextLeft + menuWidth > viewportWidth - 8) {
        nextLeft = viewportWidth - menuWidth - 8;
      }
      if (nextLeft < 0) nextLeft = 0;
      menuPosition = { top: nextTop, left: nextLeft, direction: nextDirection };
    });
  }

  function handleMenuClose() {
    showMenu = false;
    menuForm = null;
  }

  function handleAuditLogClick(form) {
    auditLogForm = form;
    showAuditLog = true;
  }

  // Add click handler to close menu when clicking outside
  function handleClickOutside(event) {
    if (showMenu && !event.target.closest('.menu-container')) {
      handleMenuClose();
    }
  }

  onMount(async () => {
    if ($selectedParticipant) {
      fetchForms(1);
    }
    // Fetch available form types and published schedule
    await Promise.all([fetchAvailableFormTypes(), fetchPublishedSchedule()]);

    // Add click outside handler
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });

  // Subscribe to selectedStudy changes
  $: {
    if ($selectedStudy) {
      fetchForms(1);
      fetchAvailableFormTypes();
      fetchPublishedSchedule();
    }
  }

  async function fetchPublishedSchedule() {
    try {
      const res = await fetch(
        `${API_BASE_URL}/schedules/schedulesByStudy?studyGUID=${$selectedStudy._id}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch study schedules');
      }

      const response = await res.json();
      const schedules = Array.isArray(response.data) ? response.data : [];

      // Find the published schedule
      publishedSchedule = schedules.find((schedule) => schedule.status === 'Published');
    } catch (err) {
      console.error('Error fetching published schedule:', err);
    }
  }

  async function fetchFormActivities() {
    if (!publishedSchedule) {
      console.error('No published schedule found');
      return;
    }

    try {
      isLoadingFormActivities = true;
      const res = await fetch(
        `${API_BASE_URL}/activities/bySchedule?scheduleGUID=${publishedSchedule._id}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch activities');
      }

      const response = await res.json();
      const allActivities = Array.isArray(response.data) ? response.data : [];

      // Filter for FORM and FORMGROUP activities
      const formTypeActivities = allActivities.filter(
        (activity) => activity.activityTypeCode === 'FORM' || activity.activityTypeCode === 'FORMGROUP'
      );

      // Normalize activities: expand FORMGROUP into individual form entries
      const normalizedActivities = await normalizeFormActivities(formTypeActivities);
      formActivities = normalizedActivities;
    } catch (err) {
      console.error('Error fetching form activities:', err);
      formActivities = [];
    } finally {
      isLoadingFormActivities = false;
    }
  }

  // Normalize form activities by expanding FORMGROUP activities into individual form entries
  async function normalizeFormActivities(activities) {
    const normalized = [];
    const formGUIDsToFetch = new Set();

    // Collect all formGUIDs from FORMGROUP activities that need name lookup
    for (const activity of activities) {
      if (activity.activityTypeCode === 'FORMGROUP' && Array.isArray(activity.formGUIDs)) {
        for (const guid of activity.formGUIDs) {
          formGUIDsToFetch.add(guid.toString ? guid.toString() : guid);
        }
      }
    }

    // Fetch form names for all GUIDs in FORMGROUP activities
    let formNameMap = {};
    if (formGUIDsToFetch.size > 0) {
      formNameMap = await fetchFormNames([...formGUIDsToFetch]);
    }

    // Process activities
    for (const activity of activities) {
      if (activity.activityTypeCode === 'FORM') {
        // Regular FORM activity - add as is
        normalized.push(activity);
      } else if (activity.activityTypeCode === 'FORMGROUP' && Array.isArray(activity.formGUIDs)) {
        // FORMGROUP activity - expand into individual entries
        for (const formGUID of activity.formGUIDs) {
          const guidStr = formGUID.toString ? formGUID.toString() : formGUID;
          const formName = formNameMap[guidStr] || 'Unknown Form';
          normalized.push({
            ...activity,
            // Override with individual form details
            formGUID: guidStr,
            formName: formName,
            // Mark as expanded from group for UI purposes
            _isFromGroup: true,
            _groupName: activity.name,
            _groupDescription: activity.description
          });
        }
      }
    }

    return normalized;
  }

  // Fetch form names for a list of GUIDs
  async function fetchFormNames(formGUIDs) {
    const nameMap = {};
    try {
      // Fetch forms in parallel batches (limit concurrent requests)
      const fetchPromises = formGUIDs.map(async (guid) => {
        try {
          const res = await fetch(`${API_BASE_URL}/forms/${guid}`);
          if (res.ok) {
            const data = await res.json();
            nameMap[guid] = data.data?.name || 'Unknown Form';
          }
        } catch (err) {
          console.warn(`Failed to fetch form name for GUID ${guid}:`, err);
        }
      });
      await Promise.all(fetchPromises);
    } catch (err) {
      console.error('Error fetching form names:', err);
    }
    return nameMap;
  }

  function openAddFormModal() {
    if (!canAddForms) {
      console.error('Insufficient permissions to add forms');
      return;
    }

    if (!$selectedParticipant || !$selectedStudy) {
      // Show error message - you might want to add a toast notification system
      console.error('Please select both a participant and a study');
      return;
    }

    if (!publishedSchedule) {
      console.error('No published schedule found for this study');
      return;
    }

    showAddFormModal = true;
    fetchFormActivities();
  }

  function closeAddFormModal() {
    showAddFormModal = false;
    formActivities = [];
  }

  async function handleAddForm(activity) {
    try {
      // Close the form selection modal
      closeAddFormModal();

      // Fetch the form definition
      await fetchFormDefinition(activity.formGUID);

      // Show the form filling modal
      showFormFillingModal = true;
    } catch (error) {
      console.error('Error adding form:', error);
    }
  }

  async function fetchFormDefinition(formGUID) {
    if (!formGUID) {
      console.error('No form GUID provided');
      return;
    }

    try {
      isLoadingFormDefinition = true;
      const res = await fetch(`${API_BASE_URL}/forms/${formGUID}`);

      if (!res.ok) {
        throw new Error('Failed to fetch form definition');
      }

      const response = await res.json();
      selectedFormDefinition = response.data;

      // Initialize form data
      initializeFormData();
    } catch (err) {
      console.error('Error fetching form definition:', err);
    } finally {
      isLoadingFormDefinition = false;
    }
  }

  async function isQuestionnaireForm(form) {
    try {
      if (!form || !form.modelGUID) {
        console.warn('❌ Form or modelGUID not found');
        return false;
      }

      // Now look up the form model using the modelGUID
      const modelRes = await fetch(`${API_BASE_URL}/lookups/FORMMODEL`);
      if (!modelRes.ok) {
        console.warn('Failed to fetch form models');
        return false;
      }

      const modelData = await modelRes.json();
      const formModels = modelData.data || [];

      // Find the form model with the given modelGUID
      const formModel = formModels.find((m) => m.id === form.modelGUID);

      if (!formModel) {
        console.warn('Form model not found');
        return false;
      }

      // Check if the form model type is "Questionnaire"
      const isQuestionnaire = formModel.code === 'QUESTIONNAIRE';
      return isQuestionnaire;
    } catch (err) {
      console.error('❌ Error checking if form is questionnaire:', err);
      return false;
    }
  }

  // Helper to normalize ObjectId-like values to plain strings
  function normalizeGUID(id) {
    if (!id) return '';
    if (typeof id === 'string') return id;
    if (id.$oid) return id.$oid;
    if (id.toHexString) return id.toHexString();
    const s = id.toString ? id.toString() : '';
    const match = s.match(/[a-fA-F0-9]{24}/);
    return match ? match[0] : s;
  }

  // Build translations list for form filling language dropdown and enrich with language details
  // NOTE: displayBase already includes the flag, e.g., "🇦🇹 German"
  async function buildFormFillingTranslations(formDefinition) {
    const result = [];
    if (!formDefinition?.translations || !Array.isArray(formDefinition.translations)) {
      return result;
    }

    // Helper to extract language name without flag for sorting
    // displayBase format is "🇦🇹 German" - we need just "German"
    function extractLanguageName(display) {
      if (!display) return '';
      return display.replace(/^[\p{Emoji}\p{Emoji_Component}\s]+/u, '').trim();
    }

    // Fetch study languages to get display names (these map to form translation languageGUIDs)
    let languagesMap = {};
    try {
      // Use study languages endpoint - translations use study-specific language GUIDs
      const studyId = $selectedStudy?._id;
      if (studyId) {
        const langRes = await fetch(`${API_BASE_URL}/languages/study/${studyId}`);
        if (langRes.ok) {
          const studyLanguages = await langRes.json();
          const languages = Array.isArray(studyLanguages) ? studyLanguages : [];
          languages.forEach(l => {
            // Study languages use languageGUID as the key
            const langGUID = normalizeGUID(l.languageGUID);
            // displayBase already includes flag, e.g., "🇦🇹 German"
            // Use it directly for display, just append the locale code
            const displayBase = l.displayBase || `${l.flag || ''} ${l.language || ''}`.trim();
            const code = l.code || '';
            const displayWithCode = code ? `${displayBase} (${code})`.trim() : displayBase;
            
            languagesMap[langGUID] = {
              code: code,
              languageName: extractLanguageName(displayBase), // For sorting
              display: displayWithCode || 'Unknown'
            };
          });
        }
      }
    } catch (err) {
      console.warn('Could not fetch study languages for dropdown:', err);
    }

    // Build translations with enriched data
    for (const t of formDefinition.translations) {
      if (t.form) {
        const normalizedLangGUID = normalizeGUID(t.languageGUID);
        const langInfo = languagesMap[normalizedLangGUID] || {};
        
        // Use display from study languages (which uses displayBase with flag)
        let displayName = langInfo.display;
        if (!displayName && t.languageDisplay) {
          // languageDisplay from backend also includes flag (it's displayBase)
          displayName = t.languageCode ? `${t.languageDisplay} (${t.languageCode})` : t.languageDisplay;
        }
        if (!displayName && t.languageCode) {
          displayName = t.languageCode;
        }
        if (!displayName) {
          displayName = 'Unknown';
        }
        
        result.push({
          languageGUID: normalizedLangGUID,
          languageCode: langInfo.code || t.languageCode || null,
          languageName: langInfo.languageName || extractLanguageName(t.languageDisplay) || '', // For sorting
          languageDisplay: displayName,
          form: t.form
        });
      }
    }

    // Sort alphabetically by language name (e.g., "Danish", "English", "German")
    result.sort((a, b) => (a.languageName || '').localeCompare(b.languageName || ''));

    return result;
  }

  // Get the form JSON for a specific language GUID from translations
  function getFormJSONForLanguage(translations, languageGUID) {
    if (!translations || !languageGUID) return null;
    const translation = translations.find(t => t.languageGUID === languageGUID);
    return translation?.form || null;
  }

  // Find the en_US language GUID from translations list
  function findEnUSLanguageGUID(translations) {
    const enUS = translations.find(t => t.languageCode === 'en_US');
    return enUS?.languageGUID || null;
  }

  // Get the appropriate translated form JSON based on participant's language
  // Priority: 1) en_US (default for form filling), 2) Participant's language, 3) first available
  async function getTranslatedFormJSON(formDefinition) {
    if (!formDefinition) return null;

    // Build enriched translations list for dropdown
    formTranslationsForFilling = await buildFormFillingTranslations(formDefinition);
    
    const participantLanguageGUID = normalizeGUID($selectedParticipant?.languageGUID);
    
    if (formTranslationsForFilling.length > 0) {
      // 1. Default to en_US for form filling
      const enUSGUID = findEnUSLanguageGUID(formTranslationsForFilling);
      if (enUSGUID) {
        selectedFillingLanguageGUID = enUSGUID;
        const enUSForm = getFormJSONForLanguage(formTranslationsForFilling, enUSGUID);
        if (enUSForm) {
          return enUSForm;
        }
      }

      // 2. Fall back to participant's language
      if (participantLanguageGUID) {
        const participantTranslation = formTranslationsForFilling.find(
          t => t.languageGUID === participantLanguageGUID
        );
        if (participantTranslation) {
          selectedFillingLanguageGUID = participantLanguageGUID;
          return participantTranslation.form;
        }
      }

      // 3. Fall back to first available translation
      const firstTranslation = formTranslationsForFilling[0];
      if (firstTranslation) {
        selectedFillingLanguageGUID = firstTranslation.languageGUID;
        return firstTranslation.form;
      }
    }

    // 4. Final fallback to Form.form
    selectedFillingLanguageGUID = null;
    formTranslationsForFilling = [];
    return formDefinition.form;
  }

  // Handle language change in form filling modal
  async function handleFillingLanguageChange(event) {
    const newLanguageGUID = event.target.value;
    if (!newLanguageGUID || newLanguageGUID === selectedFillingLanguageGUID) return;

    selectedFillingLanguageGUID = newLanguageGUID;
    const newFormJSON = getFormJSONForLanguage(formTranslationsForFilling, newLanguageGUID);
    
    if (newFormJSON) {
      // Just update the form JSON - no need to re-initialize
      // The form structure is the same, just the labels change
      selectedFormJSON = newFormJSON;
    }
  }

  async function initializeFormData() {
    if (!selectedFormDefinition) return;

    try {
      // Reset form type determination flag
      isFormTypeDetermined = false;

      // Get the appropriate translated form JSON
      const formJSON = await getTranslatedFormJSON(selectedFormDefinition);
      if (!formJSON) {
        console.error('No form JSON available');
        return;
      }

      // Store the selected translated form JSON for UI rendering
      selectedFormJSON = formJSON;

      // Parse and normalize the form JSON definition
      const formConfig = parseFormConfig(formJSON);
      if (!formConfig) {
        console.error('Invalid form JSON definition');
        return;
      }

      // Initialize form data object (but preserve existing data when editing)
      if (!isEditing) {
        formData = {};
        formErrors = {};
      }

      // Reset section navigation state
      currentSectionIndex = 0;
      formSections = [];
      isMultiSectionForm = false;

      // Check if this is a multi-section form based on form type
      if (await isQuestionnaireForm(selectedFormDefinition)) {
        // This is a questionnaire form - use multi-section layout
        isMultiSectionForm = true;

        // Extract sections from the form structure - handle multiple section types
        if (formConfig.section?.sections) {
          const mainSection = formConfig.section.sections[0];
          if (mainSection?.sections) {
            // Look for sections with various section types that indicate questionnaire sections
            const validSectionTypes = ['subscale', 'dtsqQuestions', 'questions', 'sections'];
            formSections = mainSection.sections.filter(
              (s) =>
                validSectionTypes.includes(s.sectionType) && s.sections && s.sections.length > 0
            );
          }
        }

        // If no sections found with current logic, try alternative structures
        if (formSections.length === 0) {
          // Try direct sections array
          if (formConfig.sections) {
            const validSectionTypes = ['subscale', 'dtsqQuestions', 'questions', 'sections'];
            formSections = formConfig.sections.filter(
              (s) =>
                validSectionTypes.includes(s.sectionType) && s.sections && s.sections.length > 0
            );
          }

          // Try section.sections directly
          if (formSections.length === 0 && formConfig.section?.sections) {
            const validSectionTypes = ['subscale', 'dtsqQuestions', 'questions', 'sections'];
            formSections = formConfig.section.sections.filter(
              (s) =>
                validSectionTypes.includes(s.sectionType) && s.sections && s.sections.length > 0
            );
          }
        }
      }

      // Mark form type as determined
      isFormTypeDetermined = true;

      // Extract form fields from the definition
      extractFormFields(formConfig.section);
    } catch (err) {
      console.error('Error parsing form definition:', err);
    }
  }

  function extractFormFields(section) {
    if (!section || !section.sections) return;

    section.sections.forEach((subSection) => {
      if (subSection.type === 'component' && subSection.component) {
        const component = subSection.component;
        const fieldName = component.name;

        // Initialize field with default value or empty value (but only if not editing or field doesn't exist)

        if (!isEditing || formData[fieldName] === undefined) {
          // Set appropriate default based on component type
          switch (component.componentName) {
            case 'InputMultiCheckboxesComponent':
              // Multi-checkbox should always be an array
              if (component.configuration?.defaultValue !== undefined) {
                // If defaultValue is an array, use it; otherwise wrap in array or use empty array
                const defaultVal = component.configuration.defaultValue;
                formData[fieldName] = Array.isArray(defaultVal) ? defaultVal : [];
              } else {
                // Check for options with default: true
                const defaultOptions = (component.configuration?.options || [])
                  .filter(opt => opt.default === true)
                  .map(opt => opt.code);
                formData[fieldName] = defaultOptions;
              }
              break;
            case 'InputNumberComponent':
              formData[fieldName] = component.configuration?.defaultValue ?? '';
              break;
            case 'InputDateComponent':
              formData[fieldName] = component.configuration?.defaultValue ?? '';
              break;
            case 'InputDropdownComponent':
              formData[fieldName] = component.configuration?.defaultValue ?? '';
              break;
            case 'InputTextAreaComponent':
              formData[fieldName] = component.configuration?.defaultValue ?? '';
              break;
            case 'DTSQQuestionComponent':
              formData[fieldName] = component.configuration?.defaultValue ?? '';
              break;
            default:
              formData[fieldName] = component.configuration?.defaultValue ?? '';
          }
        }
      } else if (subSection.type === 'sections' && subSection.sectionType === 'addableFields') {
        // Handle addable fields sections
        const sectionName = subSection.sectionName;
        addableFieldGroups[sectionName] = {
          section: subSection,
          groups: [0], // Start with one group
        };

        // Initialize the first group (but only if not editing)
        if (!isEditing) {
          subSection.sections.forEach((componentSection) => {
            if (componentSection.type === 'component' && componentSection.component) {
              const component = componentSection.component;
              const fieldName = `${component.name}[0]`;
              formData[fieldName] = '';
            }
          });
        }
      } else if (subSection.sections) {
        // Recursively process nested sections
        extractFormFields(subSection);
      }
    });
  }

  function setupAddableFieldGroups(section) {
    if (!section || !section.sections) return;

    section.sections.forEach((subSection) => {
      if (subSection.type === 'sections' && subSection.sectionType === 'addableFields') {
        // Handle addable fields sections - only set up structure, don't overwrite data
        const sectionName = subSection.sectionName;
        if (!addableFieldGroups[sectionName]) {
          addableFieldGroups[sectionName] = {
            section: subSection,
            groups: [],
          };
        } else {
          // Update the section data if it already exists
          addableFieldGroups[sectionName].section = subSection;
        }
      } else if (subSection.sections) {
        // Recursively process nested sections
        setupAddableFieldGroups(subSection);
      }
    });
  }

  function closeFormFillingModal() {
    showFormFillingModal = false;
    selectedFormDefinition = null;
    selectedFormJSON = null;
    formTranslationsForFilling = [];
    selectedFillingLanguageGUID = null;
    formData = {};
    formErrors = {};
    addableFieldGroups = {};
    isEditing = false;
    editingSubjectFormId = null;
    editingSubjectForm = null;

    // Reset section navigation
    currentSectionIndex = 0;
    formSections = [];
    isMultiSectionForm = false;
  }


  function validateCurrentSection() {
    if (!isMultiSectionForm || !formSections[currentSectionIndex]) {
      return true;
    }

    const currentSection = formSections[currentSectionIndex];
    const fields = renderSectionFields(currentSection);
    let isValid = true;

    fields.forEach((field) => {
      if (field.required && (!formData[field.name] || formData[field.name] === '')) {
        formErrors[field.name] = 'This field is required';
        isValid = false;
      } else {
        delete formErrors[field.name];
      }
    });

    return isValid;
  }

  function showDeleteConfirmationModal(form) {
    formToDelete = form;
    showDeleteConfirmation = true;
  }

  function safePrettyJson(raw) {
    if (raw === null || raw === undefined) return '';
    if (typeof raw !== 'string') {
      try {
        return JSON.stringify(raw, null, 2);
      } catch (e) {
        return String(raw);
      }
    }
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch (e) {
      return raw;
    }
  }

  function sanitizeJsonString(raw) {
    if (!raw || typeof raw !== 'string') return raw;
    // Replace literal newlines with escaped newlines so JSON.parse won't fail
    return raw.replace(/\r?\n/g, '\\n');
  }

  function normalizeFormConfig(formConfig) {
    if (!formConfig || typeof formConfig !== 'object') return formConfig;
    if (formConfig.section && Array.isArray(formConfig.section.sections)) return formConfig;
    if (formConfig.type === 'sections' && Array.isArray(formConfig.sections)) {
      // Defensive compatibility: accept root-level section payloads
      return { ...formConfig, section: formConfig };
    }
    return formConfig;
  }

  function parseFormConfig(rawFormConfig) {
    if (!rawFormConfig) return null;
    try {
      let parsed = rawFormConfig;
      if (typeof rawFormConfig === 'string') {
        // Pretty-printed JSON is valid as-is. sanitizeJsonString() rewrites
        // structural newlines into the two-char sequence "\n", which
        // JSON.parse then rejects. Only sanitize when the raw string is
        // actually invalid (unescaped newlines inside string values).
        try {
          parsed = JSON.parse(rawFormConfig);
        } catch {
          parsed = JSON.parse(sanitizeJsonString(rawFormConfig));
        }
        // Some imports store the payload double-encoded.
        if (typeof parsed === 'string') {
          try {
            parsed = JSON.parse(parsed);
          } catch {
            parsed = JSON.parse(sanitizeJsonString(parsed));
          }
        }
      }
      return normalizeFormConfig(parsed);
    } catch (e) {
      console.error('Error parsing form configuration:', e);
      return null;
    }
  }

  function showEditDebugInfo(error, form, source = 'error') {
    const payload = {
      error: error ? (error?.message || String(error)) : null,
      source,
      formId: form?._id || null,
      formGUID: form?.formGUID || null,
      name: form?.name || null,
      modelGUID: form?.modelGUID || null,
    };
    editDebugPayload = JSON.stringify(payload, null, 2);
    editDebugFormJSON = safePrettyJson(form?.form);
    editDebugPropertiesJSON = safePrettyJson(form?.properties);
    showEditDebugModal = true;
  }

  function closeEditDebugModal() {
    showEditDebugModal = false;
    editDebugPayload = '';
    editDebugFormJSON = '';
    editDebugPropertiesJSON = '';
  }

  function showEditDebugFromMenu(form) {
    showEditDebugInfo(null, form, 'menu');
  }

  async function handleDeleteForm() {
    if (!formToDelete) return;

    try {
      const res = await fetch(`${API_BASE_URL}/forms/subjectForm/${formToDelete._id}`, {
        method: 'DELETE',
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to delete form');
      }

      closeDeleteConfirmationModal();
      fetchForms(1); // Refresh the list
    } catch (error) {
      console.error('Error deleting form:', error);
      // TODO: Show error message to user
    }
  }

  async function handleEditForm(form) {
    if (!canEditForms) {
      console.error('Insufficient permissions to edit forms');
      return;
    }

    try {
      // Store the SubjectForm ID and document for the PUT request
      editingSubjectFormId = form._id;
      editingSubjectForm = form;

      // Check if we have a valid form definition
      let formDefinition = null;
      if (form.form && form.form.trim() !== '') {
        try {
          formDefinition = parseFormConfig(form.form);
        } catch (parseError) {
          console.warn(
            'Failed to parse saved form definition, fetching from original form:',
            parseError
          );
        }
      } else {
      }

      // If we don't have a valid form definition, fetch it from the original form
      if (!formDefinition) {
        try {
          const res = await fetch(`${API_BASE_URL}/forms/${form.formGUID}`);
          if (res.ok) {
            const originalForm = await res.json();
            formDefinition = parseFormConfig(originalForm.data.form);
            // Update the form field with the fetched definition
            form.form = originalForm.data.form;
          } else {
            throw new Error('Failed to fetch original form definition');
          }
        } catch (fetchError) {
          console.error('Error fetching original form definition:', fetchError);
          throw new Error('Unable to load form definition for editing');
        }
      }

      // Set the form definition for editing
      selectedFormDefinition = {
        _id: form.formGUID,
        name: form.name,
        form: form.form,
        modelGUID: form.modelGUID,
      };
      
      // When editing, use the saved form JSON directly (it's already the translated version)
      selectedFormJSON = form.form;

      // If modelGUID is missing, try to get it from the form definition
      if (!selectedFormDefinition.modelGUID && form.formGUID) {
        try {
          const formRes = await fetch(`${API_BASE_URL}/forms/${form.formGUID}`);
          if (formRes.ok) {
            const formData = await formRes.json();
            if (formData.data && formData.data.modelGUID) {
              selectedFormDefinition.modelGUID = formData.data.modelGUID;
            }
          }
        } catch (error) {
          console.warn('Could not fetch form modelGUID:', error);
        }
      }

      // Parse the properties to populate form data
      const properties = JSON.parse(sanitizeJsonString(form.properties));

      // Initialize form data and addable field groups
      formData = {};
      formErrors = {};
      addableFieldGroups = {};

      // Populate form data based on properties structure

      if (Array.isArray(properties)) {
        // Regular form structure
        properties.forEach((prop) => {
          if (prop.component_name) {
            formData[prop.component_name] = prop.value;
          } else if (prop.section_name && prop.values) {
            // Handle addable fields
            const sectionName = prop.section_name;
            addableFieldGroups[sectionName] = {
              section: null, // Will be set when we parse the form definition
              groups: [],
            };

            prop.values.forEach((groupData, index) => {
              addableFieldGroups[sectionName].groups.push(index);
              groupData.forEach((field) => {
                const fieldName = `${field.component_name}[${index}]`;
                formData[fieldName] = field.value;
              });
            });
          }
        });
      } else if (typeof properties === 'object') {
        // DTSQ form structure
        Object.keys(properties).forEach((key) => {
          formData[key] = properties[key];
        });
      }

      // Force reactivity by reassigning the formData object
      formData = { ...formData };

      // Set editing mode BEFORE calling initializeFormData
      isEditing = true;

      // Initialize addable field groups with proper section data
      if (formDefinition.section) {
        // Only set up the addable field groups structure, don't overwrite form data
        setupAddableFieldGroups(formDefinition.section);
      }

      // Initialize form data and determine form type for editing (but preserve existing data)
      await initializeFormData();

      // Show modal
      showFormFillingModal = true;
    } catch (error) {
      console.error('Error loading form for editing:', error);
      showEditDebugInfo(error, form);
      // Show error message to user (you might want to add a toast notification system)
      alert(`Error loading form for editing: ${error.message}. Debug details are available.`);
    }
  }



  function validateForm() {
    formErrors = {};
    let isValid = true;

    if (!selectedFormDefinition) return false;

    try {
      const formConfig = parseFormConfig(selectedFormJSON || selectedFormDefinition.form);
      if (!formConfig) {
        isValid = false;
        return isValid;
      }

      if (isMultiSectionForm) {
        // Validate all sections for multi-section forms
        formSections.forEach((section) => {
          const fields = renderSectionFields(section);
          fields.forEach((field) => {
            if (field.required && isFieldEmpty(formData[field.name], field.type)) {
              formErrors[field.name] = 'This field is required';
              isValid = false;
            }
          });
        });
      } else {
        // Use existing validation for regular forms
        validateFormFields(formConfig.section);
      }
    } catch (err) {
      console.error('Error validating form:', err);
      isValid = false;
    }

    return isValid;
  }

  function validateFormFields(section) {
    if (!section || !section.sections) return;

    section.sections.forEach((subSection) => {
      if (subSection.type === 'component' && subSection.component) {
        const component = subSection.component;
        const fieldName = component.name;
        const value = formData[fieldName];
        const fieldType = getFieldType(component.componentName);

        // Check required validation
        const requiredValidation = component.validations?.find((v) => v.required);
        if (requiredValidation && isFieldEmpty(value, fieldType)) {
          formErrors[fieldName] = 'This field is required';
        }
      } else if (subSection.sections) {
        validateFormFields(subSection);
      }
    });
  }

  async function handleSubmitForm() {
    if (!validateForm()) {
      return;
    }

    if (isEditing && !canEditForms) {
      console.error('Insufficient permissions to edit forms');
      return;
    }

    if (!isEditing && !canAddForms) {
      console.error('Insufficient permissions to add forms');
      return;
    }

    try {
      // Prepare the properties data based on form type
      let properties;

      if (selectedFormDefinition.name.includes('DTSQ')) {
        // For DTSQ forms, create a simple object with question values
        properties = {};
        Object.keys(formData).forEach((key) => {
          if (key.startsWith('dtsq_question_')) {
            properties[key] = formData[key];
          }
        });
      } else {
        // For other forms, create the complex structure with sections
        properties = [];

        // Add regular fields
        Object.keys(formData).forEach((key) => {
          if (!key.includes('[') && !key.startsWith('dtsq_question_')) {
            properties.push({
              component_name: key,
              value: formData[key],
            });
          }
        });

        // Add addable field groups
        Object.keys(addableFieldGroups).forEach((sectionName) => {
          const section = addableFieldGroups[sectionName];
          const values = [];

          section.groups.forEach((groupIndex) => {
            const groupData = [];
            if (!section.section || !section.section.sections) {
              console.error('Missing section data for:', sectionName);
              return;
            }
            section.section.sections.forEach((componentSection) => {
              if (componentSection.type === 'component' && componentSection.component) {
                const component = componentSection.component;
                const fieldName = `${component.name}[${groupIndex}]`;
                if (formData[fieldName] !== undefined) {
                  groupData.push({
                    component_name: component.name,
                    value: formData[fieldName],
                  });
                }
              }
            });
            if (groupData.length > 0) {
              values.push(groupData);
            }
          });

          if (values.length > 0) {
            properties.push({
              section_name: sectionName,
              values: values,
            });
          }
        });
      }

      // Derive formDate from the first InputDateComponent if present (interpret in participant timezone)
      let derivedFormDate = null;
      try {
        const formConfig = parseFormConfig(selectedFormJSON || selectedFormDefinition.form);
        if (!formConfig) {
          throw new Error('Invalid form definition');
        }
        const participantTz = $selectedParticipant?.timezoneCode || (Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');

        function toUtcIsoFromTimezoneLocal(naiveLocalStr, timeZone) {
          if (!naiveLocalStr || typeof naiveLocalStr !== 'string') return null;
          // naiveLocalStr is typically "YYYY-MM-DDTHH:mm" (from datetime-local). Support seconds if present.
          const [datePart, timePartRaw] = naiveLocalStr.split('T');
          if (!datePart || !timePartRaw) return null;
          const [y, m, d] = datePart.split('-').map((n) => parseInt(n, 10));
          const [hh, mm, ss] = timePartRaw.split(':').map((n) => parseInt(n, 10));
          const sec = isNaN(ss) ? 0 : ss;

          // Build a UTC guess for the same wall time numbers
          const utcGuessMs = Date.UTC(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, sec);
          const guessDate = new Date(utcGuessMs);

          // Get the timezone offset string for this guess (e.g., GMT+05:30)
          const fmt = new Intl.DateTimeFormat('en-US', {
            timeZone,
            timeZoneName: 'shortOffset',
            hour12: false,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          });
          const parts = fmt.formatToParts(guessDate);
          const tzNamePart = parts.find((p) => p.type === 'timeZoneName');
          const tzName = tzNamePart?.value || '';
          // Extract +HH:MM or -HH:MM from strings like GMT+05:30 or UTC-08:00
          const match = tzName.match(/([+-])(\d{1,2}):(\d{2})/);
          let offset = 'Z';
          if (match) {
            const sign = match[1];
            const hhOff = match[2].padStart(2, '0');
            const mmOff = match[3].padStart(2, '0');
            offset = `${sign}${hhOff}:${mmOff}`;
          }

          // Construct an ISO-like string with explicit offset
          const yStr = String(y).padStart(4, '0');
          const mStr = String(m).padStart(2, '0');
          const dStr = String(d).padStart(2, '0');
          const hhStr = String(hh || 0).padStart(2, '0');
          const mmStr = String(mm || 0).padStart(2, '0');
          const ssStr = String(sec).padStart(2, '0');
          const isoWithOffset = `${yStr}-${mStr}-${dStr}T${hhStr}:${mmStr}:${ssStr}${offset}`;
          const dt = new Date(isoWithOffset);
          return isNaN(dt.getTime()) ? null : dt.toISOString();
        }

        function findFirstDateField(section) {
          if (!section || !section.sections) return null;
          for (const subSection of section.sections) {
            if (subSection.type === 'component' && subSection.component) {
              if (subSection.component.componentName === 'InputDateComponent') {
                return subSection.component.name;
              }
            } else if (subSection.sections) {
              const nested = findFirstDateField(subSection);
              if (nested) return nested;
            }
          }
          return null;
        }
        const firstDateFieldName = findFirstDateField(formConfig.section);
        if (firstDateFieldName && formData[firstDateFieldName]) {
          derivedFormDate = toUtcIsoFromTimezoneLocal(formData[firstDateFieldName], participantTz);
        }
      } catch (e) {
        // ignore derivation errors and fall back to existing behavior
      }

      // Create the SubjectForm document
      // Save the translated form JSON so it displays correctly when viewed later
      const formJSONToSave = selectedFormJSON || selectedFormDefinition.form;
      const subjectFormData = {
        formGUID: selectedFormDefinition._id,
        name: selectedFormDefinition.name,
        form: formJSONToSave, // Save the translated form definition
        properties: JSON.stringify(properties),
        subjectGUID: $selectedParticipant._id,
        // Participant's zone, not the coordinator's browser: reports resolve a
        // form's local day from this field, and formDate below is already
        // derived in the participant's zone.
        timezone:
          $selectedParticipant?.timezoneCode ||
          Intl.DateTimeFormat().resolvedOptions().timeZone ||
          'UTC',
        formDate:
          isEditing && editingSubjectFormId
            ? // For edits, prefer derived date if available; else preserve existing; else now
              derivedFormDate || (editingSubjectForm && editingSubjectForm.formDate
                ? editingSubjectForm.formDate
                : new Date().toISOString())
            : // For new forms, prefer first date field if available; else now
              derivedFormDate || new Date().toISOString(),
      };

      // Ensure we have a valid form definition to save
      if (!formJSONToSave || formJSONToSave.trim() === '') {
        throw new Error('Form definition is missing or invalid');
      }

      const url = isEditing
        ? `${API_BASE_URL}/forms/subjectForm/${editingSubjectFormId}`
        : `${API_BASE_URL}/forms/subjectForm`;

      const method = isEditing ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(subjectFormData),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to submit form');
      }

      const result = await res.json();

      // Close the modal
      closeFormFillingModal();

      // Refresh the forms list
      fetchForms(1);
    } catch (error) {
      console.error('Error submitting form:', error);
      // TODO: Show error message to user
    }
  }


  function renderSectionFields(section) {
    const fields = [];

    if (!section || !section.sections) return fields;

    section.sections.forEach((subSection) => {
      if (subSection.type === 'component' && subSection.component) {
        const component = subSection.component;
        const field = {
          name: component.name,
          header: component.header,
          type: getFieldType(component.componentName),
          placeholder: component.placeholder,
          required: component.validations?.some((v) => v.required) || false,
          options: component.configuration?.options || [],
          rank: component.rank || null,
        };
        fields.push(field);
      }
    });

    return fields;
  }

  function getFieldType(componentName) {
    switch (componentName) {
      case 'InputTextComponent':
        return 'text';
      case 'InputNumberComponent':
        return 'number';
      case 'InputDateComponent':
        return 'date';
      case 'InputTextAreaComponent':
        return 'textarea';
      case 'InputDropdownComponent':
        return 'select';
      case 'InputMultiCheckboxesComponent':
        return 'multiCheckbox';
      case 'DTSQQuestionComponent':
        return 'dtsq';
      default:
        return 'text';
    }
  }

  // Check if a field value is empty (handles arrays for multiCheckbox)
  function isFieldEmpty(value, fieldType) {
    if (value === null || value === undefined) return true;
    if (fieldType === 'multiCheckbox') {
      return !Array.isArray(value) || value.length === 0;
    }
    if (typeof value === 'string') return value.trim() === '';
    return false;
  }

  async function fetchAvailableFormTypes() {
    try {
      const url = `${API_BASE_URL}/forms/types?studyGUID=${$selectedStudy._id}`;

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch form types');
      }

      const responseData = await res.json();
      availableFormTypes = responseData.data || [];
    } catch (err) {
      console.error('Error fetching form types:', err);
      availableFormTypes = [];
    }
  }

  // SP-4: timezone the day filter is interpreted in. Prefer the selected
  // participant's own zone; fall back to the site's, then the browser's.
  // The backend still prefers each subject's TZ.code per row — this only
  // covers subjects with no TZ record (and multi-participant views).
  function participantTimezone() {
    return (
      $selectedParticipant?.timezoneCode ||
      $selectedSite?.timezoneCode ||
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      'UTC'
    );
  }

  async function fetchForms(page = 1) {
    try {
      isLoading = true;
      let url = `${API_BASE_URL}/forms/userForms?page=${page}&pageSize=${limit}&studyGUID=${$selectedStudy._id}&subjectStatus=${selectedStatus}`;

      if ($selectedParticipant) {
        url += `&subjectGUID=${$selectedParticipant._id}`;
      }

      if ($selectedSite) {
        url += `&siteGUID=${$selectedSite._id}`;
      }

      if (selectedType !== 'ALL') {
        url += `&type=${selectedType}`;
      }

      if (selectedDate && dayRange !== 0) {
        url += `&day=${selectedDate}&days=${dayRange}`;
        // SP-4: the backend evaluates the day window in the participant's own
        // timezone (TZ.code); this is the fallback for subjects with no TZ row.
        // Without it an evening submission lands on the next UTC day and the
        // grid looks empty until the date filter is advanced.
        url += `&timezone=${encodeURIComponent(participantTimezone())}`;
      }

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch forms');
      }

      const responseData = await res.json();

      resultCount = responseData.totalCount || 0;
      currentPage = responseData.page || 1;
      totalPages = responseData.totalPages || 1;

      forms = responseData.data;
    } catch (err) {
      console.error('Error fetching forms:', err);
      forms = [];
    } finally {
      isLoading = false;
    }
  }

  function handleRefresh() {
    fetchForms(1);
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchForms(newPage);
    }
  }

  function isValidDate(dateString) {
    // Updated regex to handle fractional seconds more flexibly
    const iso8601Regex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/;
    return iso8601Regex.test(dateString);
  }

  function flattenData(data) {
    const result = [];

    if (Array.isArray(data)) {
      data.forEach((item) => {
        if (item.value && typeof item.value === 'object' && !Array.isArray(item.value)) {
          // Handle object values (old format for multi-select)
          for (const [key, value] of Object.entries(item.value)) {
            if (value === true) {
              result.push({ component_name: `${item.component_name}.${key}`, value });
            }
          }
        } else if (Array.isArray(item.value)) {
          // Handle array values (new format for multi-checkbox)
          // Expand array to individual boolean entries for compatibility
          item.value.forEach((code) => {
            result.push({ component_name: `${item.component_name}.${code}`, value: true });
          });
          // Also store the original array for direct access
          result.push({ component_name: item.component_name, value: item.value, _isArray: true });
        } else if (item.values && Array.isArray(item.values)) {
          item.values.forEach((subArray, index) => {
            subArray.forEach((subItem) => {
              result.push({
                component_name: `${item.section_name}[${index}].${subItem.component_name}`,
                value: subItem.value,
              });
            });
          });
        } else {
          // Check if the value is a date string
          if (typeof item.value === 'string' && isValidDate(item.value)) {
            result.push({ ...item, value: new Date(item.value) });
          } else if (typeof item.value === 'string') {
            // Handle strings that might contain newlines
            result.push({ ...item, value: item.value.replace(/\n/g, '') });
          } else {
            result.push(item);
          }
        }
      });
    } else if (typeof data === 'object' && data !== null) {
      for (const [key, value] of Object.entries(data)) {
        result.push({ component_name: key, value });
      }
    }

    return result;
  }

  function closeModal() {
    isModalOpen = false;
    modalData = [];
    modalTitle = 'Title';
    modalFormDefinition = null;
    modalDates = null;
    modalFormTranslations = [];
    modalParticipantLanguageGUID = null;
  }

  function showModal(title, data, formDefinition = null, dates = null, formTranslations = [], participantLanguageGUID = null) {
    try {
      if (!data) {
        modalData = [];
      } else {
        modalData = flattenData(JSON.parse(sanitizeJsonString(data)));
      }
      modalFormDefinition = formDefinition;
      modalDates = dates;
      modalFormTranslations = formTranslations;
      modalParticipantLanguageGUID = participantLanguageGUID;
      isModalOpen = true;
      modalTitle = title;
    } catch (e) {
      console.error('Error showing modal:', e);
      modalData = [];
      modalFormDefinition = null;
      modalDates = dates;
      modalFormTranslations = formTranslations;
      modalParticipantLanguageGUID = participantLanguageGUID;
      isModalOpen = true;
      modalTitle = title;
    }
  }

  function handleDateChange() {
    fetchForms(1);
  }

  // SPTL-484: diary export cap. Configurable via
  // `studyProperties.reports.reportSettings.diaryExportMaxRows` — default
  // 99,999 per PM so CGM / diary / BGM exports share the same ceiling
  // (was hard-coded 1,000 pre-SPTL-484; SAB201 clients hit the cap at
  // ~1,800 diary items).
  $: diaryExportMaxRows =
    $studyProperties?.reports?.reportSettings?.diaryExportMaxRows || 99999;

  // SPTL-484 (round 2): fetch the export in chunks. A single request for
  // 10,000+ forms — each carrying its full properties payload — failed
  // (QA saw an empty Excel file: the error was swallowed and [] exported).
  // 1,000/request is the QA-verified working size.
  const DIARY_EXPORT_CHUNK = 1000;

  async function fetchAllForms() {
    try {
      const all = [];
      for (let page = 1; all.length < diaryExportMaxRows; page++) {
        let url = `${API_BASE_URL}/forms/userForms?page=${page}&pageSize=${DIARY_EXPORT_CHUNK}&studyGUID=${$selectedStudy._id}&subjectStatus=${selectedStatus}`;

        if ($selectedParticipant) {
          url += `&subjectGUID=${$selectedParticipant._id}`;
        }

        if ($selectedSite) {
          url += `&siteGUID=${$selectedSite._id}`;
        }

        if (selectedType !== 'ALL') {
          url += `&type=${selectedType}`;
        }

        if (selectedDate && dayRange !== 0) {
          url += `&day=${selectedDate}&days=${dayRange}`;
          // SP-4 — keep the export's window identical to the grid's.
          url += `&timezone=${encodeURIComponent(participantTimezone())}`;
        }

        const res = await fetch(url);

        if (!res.ok) {
          throw new Error(`Failed to fetch forms (page ${page})`);
        }

        const result = await res.json();
        const chunk = result.data || [];
        all.push(...chunk);
        if (chunk.length < DIARY_EXPORT_CHUNK) break;
      }
      return all.slice(0, diaryExportMaxRows);
    } catch (err) {
      console.error('Error fetching all forms:', err);
      return [];
    }
  }

  async function handleExportExcel() {
    try {
      isExporting = true;
      const allForms = await fetchAllForms();

      // Format data for Excel export
      const formattedData = [];

      allForms.forEach((form) => {
        const formData = (() => {
          try {
            if (!form.F.properties) return [];
            const cleanProperties = form.F.properties.replace(/\n/g, '\\n');
            return flattenData(JSON.parse(cleanProperties));
          } catch (e) {
            console.error('Error parsing form properties:', e, form);
            return [];
          }
        })();

        // Base row data that will be repeated for each question
        const baseRow = {
          Site: form.SI?.siteId || 'n/a',
          Subject: form.S?.subjectId || 'n/a',
          Status: form.SS?.label || 'n/a',
          'Form Name': form.F.name,
          'Created (Local)': form.F.received
            ? formatToLocalDateTime(form.F.received, form.TZ?.code || 'UTC')
            : '',
          Timezone: form.TZ?.code || 'UTC',
          'Form Date (Local)': getFormDate(form.F.name, formData, form.TZ?.code || 'UTC', form.F, formDatePriority),
          'SubjectForm Timezone': form.F.timezone || 'UTC',
          'Form Date (Local - SubjectForm TZ)': getFormDate(form.F.name, formData, form.F.timezone || 'UTC', form.F, formDatePriority),
          'Form Date (UTC)': getFormDate(form.F.name, formData, 'UTC', form.F, formDatePriority),
        };

        if (form.F.name.includes('DTSQ') || form.F.name.includes('PedsQL')) {
          // For DTSQ and PedsQL forms, create one row per question
          // Use en_US form definition for consistent English labels in exports
          const enFormDef = getEnglishFormDefinition(form.FD);
          const formDefinitionData = enFormDef ? extractDTSQQuestionsFromForm(enFormDef) : { questionHeaders: {}, questionOptions: {}, questionSections: {}, fieldLabels: {} };
          const questionHeaders = formDefinitionData.questionHeaders || {};
          const questionOptions = formDefinitionData.questionOptions || {};
          const questionSections = formDefinitionData.questionSections || {};
          const fieldLabels = formDefinitionData.fieldLabels || {};
          
          const questions = formData.filter((d) =>
            d.component_name.startsWith('dtsq_question_') || d.component_name.startsWith('pedsql_')
          );
          questions.forEach((question) => {
            // Use en_US form definition label
            const translatedLabel =
              questionHeaders[question.component_name] ||
              question.component_name;
            
            // Get option label if available
            const options = questionOptions[question.component_name];
            const optionLabel = options && options[String(question.value)] 
              ? options[String(question.value)]
              : question.value;
            
            formattedData.push({
              ...baseRow,
              Summary: `${translatedLabel}: ${optionLabel}`,
            });
          });
        } else {
          // For non-DTSQ forms, use the original format
          // Use FD (Form Definition) for en_US labels to ensure consistent English in exports
          formattedData.push({
            ...baseRow,
            Summary: getFormSummary(form.F.name, formData, getEnglishLabelsFromFD(form.FD)),
          });
        }
      });

      // Create filename
      const filename = generateExportFilename({
        protocolNumber: $selectedStudy?.protocolNumber,
        siteId: $selectedSite?.siteId,
        subjectId: $selectedParticipant?.subjectId,
        reportType: 'DiaryEntries',
      });
      exportToExcel(formattedData, filename);
    } catch (error) {
      console.error('Error in handleExportExcel:', error);
    } finally {
      isExporting = false;
    }
  }

  // Clean HTML tags from text
  function stripHtmlTags(text) {
    if (!text) return '';
    return text
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
      .trim();
  }

  // Extract labels/options/sections from form definition. Includes general field labels for all components
  function extractDTSQQuestionsFromForm(formDefinition) {
    if (!formDefinition) return { questionHeaders: {}, questionOptions: {}, questionSections: {}, fieldLabels: {} };
    try {
      const formDef = parseFormConfig(formDefinition);
      if (!formDef) {
        return { questionHeaders: {}, questionOptions: {}, questionSections: {}, fieldLabels: {} };
      }
      const questionHeaders = {}; // Maps question name to header text
      const questionOptions = {}; // Maps question name to { code: label }
      const questionSections = {}; // Maps question name to section header
      const fieldLabels = {}; // Maps ANY component name to header/label. Also maps option codes via `${component.name}.${code}` -> label
      
      function extractQuestions(section, currentSectionHeader = null) {
        if (!section || !section.sections) return;
        
        // Update current section header if this section has one
        let sectionHeader = currentSectionHeader;
        if (section.header) {
          sectionHeader = stripHtmlTags(section.header);
        }
        
        section.sections.forEach((subSection) => {
          if (subSection.type === 'component' && subSection.component) {
            const component = subSection.component;
            if (component.name) {
              const labelText = component.header || component.placeholder || component.label;
              if (labelText) {
                fieldLabels[component.name] = stripHtmlTags(labelText);
              }
              // Also map option codes (e.g., hypo_symptoms.hunger) to their labels
              if (component.configuration && Array.isArray(component.configuration.options)) {
                component.configuration.options.forEach((opt) => {
                  if (opt && opt.code) {
                    fieldLabels[`${component.name}.${opt.code}`] = opt.label || opt.code;
                  }
                });
              }
            }
            // Extract DTSQ and PedsQL questions
            if (component.componentName === 'DTSQQuestionComponent' && component.name) {
              questionHeaders[component.name] = component.header || '';
              if (sectionHeader) {
                questionSections[component.name] = sectionHeader;
              }
              // Extract options
              if (component.configuration && component.configuration.options) {
                const optionsMap = {};
                component.configuration.options.forEach(option => {
                  optionsMap[option.code] = option.label;
                });
                questionOptions[component.name] = optionsMap;
              }
            }
            // Extract PedsQL questions (checking if name starts with pedsql_)
            else if (component.name && component.name.startsWith('pedsql_')) {
              questionHeaders[component.name] = component.header || '';
              if (sectionHeader) {
                questionSections[component.name] = sectionHeader;
              }
              // Extract options
              if (component.configuration && component.configuration.options) {
                const optionsMap = {};
                component.configuration.options.forEach(option => {
                  optionsMap[option.code] = option.label;
                });
                questionOptions[component.name] = optionsMap;
              }
            }
          } else if (subSection.sections) {
            // Recursively process nested sections, passing the current section header
            extractQuestions(subSection, sectionHeader);
          }
        });
      }
      
      if (formDef.section) {
        extractQuestions(formDef.section);
      }
      
      return { questionHeaders, questionOptions, questionSections, fieldLabels };
    } catch (e) {
      console.error('Error parsing form definition for questions:', e);
      return { questionHeaders: {}, questionOptions: {}, questionSections: {}, fieldLabels: {} };
    }
  }

  // Removed legacy DTSQ fallback map; labels come from form definitions

  // Get en_US form definition from FD (Form Definition) for consistent table display
  // This ensures the table always shows English labels regardless of what language the form was filled in
  function getEnglishFormDefinition(formDefinition) {
    if (!formDefinition) return null;
    
    // If FD has translations, find en_US
    if (formDefinition.translations && Array.isArray(formDefinition.translations)) {
      const enTranslation = formDefinition.translations.find(t => t.languageCode === 'en_US');
      if (enTranslation && enTranslation.form) {
        return enTranslation.form;
      }
      // Fallback to first translation if no en_US
      if (formDefinition.translations.length > 0 && formDefinition.translations[0].form) {
        return formDefinition.translations[0].form;
      }
    }
    
    // Fallback to the form field itself
    return formDefinition.form || null;
  }
  
  // Extract labels from Form Definition using en_US translation
  function getEnglishLabelsFromFD(formDefinition) {
    const enForm = getEnglishFormDefinition(formDefinition);
    if (!enForm) return {};
    return extractDTSQQuestionsFromForm(enForm).fieldLabels;
  }

  function getFormSummary(formName, formData, labelsMap = {}) {
    if (formName.includes('FSBS')) {
      const reading = formData.find((d) => d.component_name === 'fsbsReading')?.value || '-';
      const isHypo = formData.find((d) => d.component_name === 'fsbsReason.hypoglycemic')?.value;
      const reasons = formData
        .filter((d) => d.component_name.startsWith('fsbsReason.') && d.value === true)
        .map((d) => {
          let label =
            labelsMap[d.component_name] ||
            d.component_name.split('.')[1]?.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()) ||
            d.component_name;
          return label.replace(/, specify$/i, '');
        });
      const reasonSuffix = reasons.length ? ` (Reason: ${reasons.join(', ')})` : '';
      const hypoSuffix = isHypo ? ' (Hypoglycemic)' : '';
      if (reasonSuffix) {
        return `${reading} mg/dL ${reasonSuffix.trim()}`.replace(/\s+/g, ' ');
      }
      if (hypoSuffix) {
        return `${reading} mg/dL ${hypoSuffix.trim()}`.replace(/\s+/g, ' ');
      }
      return `${reading} mg/dL`;
    } else if (formName.includes('Food')) {
      const foodItems = formData.filter((d) => d.component_name.includes('foodItems'));
      const foodSummary = [...Array(Math.floor(foodItems.length / 2))]
        .map((_, i) => {
          const foodItem =
            formData.find((d) => d.component_name === `foodItems[${i}].FoodItem`)?.value || '';
          const percentage = formData.find(
            (d) => d.component_name === `foodItems[${i}].FoodPercentage`
          )?.value;
          return `${foodItem.trim().replace(/^\w/, (c) => c.toUpperCase())}: ${percentage}%`;
        })
        .join(', ');

      const restaurant = formData.find((d) => d.component_name === 'restaurant')?.value;
      const restaurantName = formData.find((d) => d.component_name === 'restaurantName')?.value;
      return `${foodSummary}${restaurant === 'yes' && restaurantName ? ` @ ${restaurantName}` : ''}`;
    } else if (formName.includes('Starch')) {
      let starchType = '';
      if (formData.find((d) => d.component_name === 'StarchType.glycosade')?.value) {
        starchType = 'Glycosade';
      } else if (formData.find((d) => d.component_name === 'StarchType.cornstarch')?.value) {
        starchType = 'Cornstarch';
      } else if (formData.find((d) => d.component_name === 'StarchType.tapioca')?.value) {
        starchType = 'Tapioca';
      } else if (formData.find((d) => d.component_name === 'StarchType.other')?.value) {
        starchType = formData.find((d) => d.component_name === 'OtherStarch')?.value;
      }
      const grams = formData.find((d) => d.component_name === 'StarchGrams')?.value || '-';
      return `${starchType} ${grams}g`;
    } else if (formName.includes('Ketone')) {
      const value = formData.find((d) => d.component_name === 'KetoneValue')?.value || '-';
      const reasons = formData
        .filter((d) => d.component_name.startsWith('Reason.') && d.value === true)
        .map((d) => {
          let label =
            labelsMap[d.component_name] ||
            d.component_name.split('.')[1]?.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()) ||
            d.component_name;
          return label.replace(/, specify$/i, '');
        });
      const reasonSuffix = reasons.length ? ` (Reason: ${reasons.join(', ')})` : '';
      return `${value} mmol/L${reasonSuffix}`;
    } else if (formName.includes('Blood Glucose')) {
      const reading = formData.find((d) => d.component_name === 'fsbsReading')?.value || '-';
      return `${reading} mg/dL`;
    } else if (formName.includes('Hypo')) {
      // Get OtherComments value if present
      const otherComments = formData.find((d) => d.component_name === 'OtherComments')?.value || '';
      
      // Handle both new array format and old boolean format
      const arrayEntry = formData.find((d) => d._isArray && Array.isArray(d.value));
      if (arrayEntry && arrayEntry.value.length > 0) {
        // New format: array of codes
        const labels = arrayEntry.value.map((code) => {
          // For "other" option, show the OtherComments value instead
          if (code === 'other' && otherComments) {
            return otherComments;
          }
          const fullKey = `${arrayEntry.component_name}.${code}`;
          let label = labelsMap[fullKey] || code.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
          return label.replace(/, specify$/i, '');
        });
        if (otherComments && !arrayEntry.value.includes('other')) {
          labels.push(`Other: ${otherComments}`);
        }
        return labels.join(', ');
      }
      // Old format: individual boolean entries
      const selected = formData.filter((d) => d.value === true && !d._isArray);
      const labels = selected.map((d) => {
        // For "other" option, show the OtherComments value instead
        if (d.component_name.endsWith('.other') && otherComments) {
          return otherComments;
        }
        let label = labelsMap[d.component_name] || d.component_name.split('.')[1]?.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()) || d.component_name;
        return label.replace(/, specify$/i, '');
      });
      const hasOtherSelected = selected.some((d) => d.component_name.endsWith('.other'));
      if (otherComments && !hasOtherSelected) {
        labels.push(`Other: ${otherComments}`);
      }
      return labels.length > 0 ? labels.join(', ') : 'Click to view details';
    } else if (formName.includes('Insulin')) {
      const units = formData.find((d) => d.component_name === 'Units')?.value || '-';
      const drugName = formData.find((d) => d.component_name === 'nameOfDrug')?.value || '';
      
      // Route can be stored in two formats:
      // 1. New format: Route = 'insulinSelfInjected' (dropdown selection)
      // 2. Old format: Route.insulinSelfInjected = true (boolean checkbox)
      let route = '';
      
      // First try new format (dropdown string value)
      const routeDropdown = formData.find((d) => d.component_name === 'Route' && typeof d.value === 'string');
      if (routeDropdown && routeDropdown.value) {
        const routeCode = routeDropdown.value;
        // Try to get translated label from labelsMap
        const translatedLabel = labelsMap[`Route.${routeCode}`];
        if (translatedLabel) {
          route = translatedLabel;
        } else if (routeCode.startsWith('insulin')) {
          // Fallback: format the code nicely
          const routeName = routeCode.replace(/^insulin/, '');
          route = routeName
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
        } else {
          route = routeCode;
        }
      } else {
        // Fallback to old format: Route.insulinInhaler, Route.insulinPump, etc.
        const routeEntry = formData.find((d) => d.component_name.startsWith('Route.insulin') && d.value === true);
        if (routeEntry) {
          // Try to get translated label from labelsMap
          const translatedLabel = labelsMap[routeEntry.component_name];
          if (translatedLabel) {
            route = translatedLabel;
          } else {
            const routeKey = routeEntry.component_name.replace('Route.', '');
            if (routeKey.startsWith('insulin')) {
              const routeName = routeKey.replace(/^insulin/, '');
              route = routeName
                .replace(/([a-z])([A-Z])/g, '$1 $2')
                .replace(/^./, (str) => str.toUpperCase())
                .trim();
            }
          }
        }
      }
      
      const parts = [];
      if (units !== '-') parts.push(`${units} units`);
      if (drugName) parts.push(drugName);
      if (route) parts.push(route);
      return parts.length > 0 ? parts.join(', ') : 'Click to view details';
    } else if (formName.includes('Immunosuppression')) {
      const medications = formData
        .filter((d) => d.value === true)
        .map((d) => d.component_name.split('.')[1])
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
        .join(', ');
      return medications;
    } else {
      return 'Click to view details';
    }
  }

  // Helper function to extract date from form properties based on form name
  function extractDateFromProperties(formName, formData) {
    let dateValue = null;

    if (formName.includes('FSBS')) {
      dateValue = formData.find((d) => d.component_name === 'fsbsDate')?.value;
    } else if (formName.includes('Food')) {
      dateValue = formData.find((d) => d.component_name === 'FoodIntakeDate')?.value;
    } else if (formName.includes('Starch')) {
      dateValue = formData.find((d) => d.component_name === 'StarchDate')?.value;
    } else if (formName.includes('Ketone')) {
      dateValue = formData.find((d) => d.component_name === 'KetoneDate')?.value;
    } else if (formName.includes('Symptoms')) {
      dateValue = formData.find((d) => d.component_name === 'SymptomDate')?.value;
    } else if (formName.includes('Action') && !formName.includes('Actions')) {
      dateValue = formData.find((d) => d.component_name === 'ActionDate')?.value;
    }

    // If no form-specific date found, check for generic "Date" field
    if (!dateValue) {
      dateValue = formData.find((d) => d.component_name === 'Date')?.value;
    }

    return dateValue;
  }

  // Format a local datetime string (already in local time, no timezone conversion needed)
  function formatLocalDateTime(localDateString) {
    if (!localDateString) return '';
    try {
      // Parse the local datetime string and format it nicely
      // The date is already local, so we just need to display it
      const date = new Date(localDateString);
      if (isNaN(date.getTime())) return '';
      
      return date.toLocaleString("en-US", {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error("Error formatting local date:", error);
      return '';
    }
  }

  function getFormDate(formName, formData, timezone, formDocument = null, priorityMode = 'formDate') {
    if (priorityMode === 'properties') {
      // Check properties first, then fall back to formDocument.formDate
      const dateValue = extractDateFromProperties(formName, formData);
      if (dateValue) {
        // Properties date is stored with timezone info (when available),
        // so format it using the form timezone for display.
        return formatToLocalDateTime(dateValue, timezone);
      }
      // Fallback to formDate (stored as UTC, needs conversion)
      if (formDocument && formDocument.formDate) {
        return formatToLocalDateTime(formDocument.formDate, timezone);
      }
    } else {
      // Default: check formDocument.formDate first (eCOA compliant approach)
      if (formDocument && formDocument.formDate) {
        // formDate is stored as UTC, needs timezone conversion
        return formatToLocalDateTime(formDocument.formDate, timezone);
      }
      // Fallback to properties (already local - just format it)
      const dateValue = extractDateFromProperties(formName, formData);
      if (dateValue) {
        return formatToLocalDateTime(dateValue, timezone);
      }
    }

    return '';
  }

  // Helper to normalize ObjectId-like values to plain strings
  function normalizeIdForTranslation(id) {
    if (!id) return '';
    if (typeof id === 'string') return id;
    if (id.$oid) return id.$oid;
    if (id.toHexString) return id.toHexString();
    const s = id.toString ? id.toString() : '';
    const match = s.match(/[a-fA-F0-9]{24}/);
    return match ? match[0] : s;
  }

  // Build translations array from Form Definition (FD) for the modal
  // Uses only the translations array since it contains ALL language versions
  // The backend enriches translations with languageCode, languageDisplay, and flag
  // NOTE: languageDisplay (displayBase) already includes the flag, e.g., "🇦🇹 German"
  function buildFormTranslations(formDefinition, subjectLanguage) {
    const translations = [];
    
    if (!formDefinition) return translations;
    
    // Helper to build display name - displayBase already includes flag
    // Just append the locale code, e.g., "🇦🇹 German" -> "🇦🇹 German (de_AT)"
    function buildDisplayName(display, code) {
      if (display && code) {
        return `${display} (${code})`.trim();
      }
      return display || code || 'Unknown';
    }
    
    // Helper to extract language name without flag for sorting
    // displayBase format is "🇦🇹 German" - we need just "German"
    function extractLanguageName(display) {
      if (!display) return '';
      // Remove leading emoji/flag (emoji is usually followed by space)
      return display.replace(/^[\p{Emoji}\p{Emoji_Component}\s]+/u, '').trim();
    }
    
    // Use the translations array - it contains ALL language versions including en_US
    // The top-level form field is a duplicate of one translation, so we don't add it separately
    if (formDefinition.translations && Array.isArray(formDefinition.translations)) {
      formDefinition.translations.forEach(t => {
        if (t.languageGUID && t.form) {
          translations.push({
            languageGUID: normalizeIdForTranslation(t.languageGUID),
            form: t.form,
            languageCode: t.languageCode || null,
            languageName: extractLanguageName(t.languageDisplay), // For sorting
            languageDisplay: buildDisplayName(t.languageDisplay, t.languageCode)
          });
        }
      });
    }
    
    // Fallback: if no translations array but there's a top-level form, use it
    if (translations.length === 0 && formDefinition.languageGUID && formDefinition.form) {
      translations.push({
        languageGUID: normalizeIdForTranslation(formDefinition.languageGUID),
        form: formDefinition.form,
        languageCode: formDefinition.languageCode || null,
        languageName: extractLanguageName(formDefinition.languageDisplay),
        languageDisplay: buildDisplayName(formDefinition.languageDisplay, formDefinition.languageCode)
      });
    }
    
    // If we have subject language info (SL), enrich the matching translation
    // with participant language details (fallback if backend didn't provide it)
    if (subjectLanguage && subjectLanguage._id) {
      const slId = normalizeIdForTranslation(subjectLanguage._id);
      const matchingTranslation = translations.find(t => t.languageGUID === slId);
      if (matchingTranslation && !matchingTranslation.languageCode) {
        matchingTranslation.languageCode = subjectLanguage.code || null;
        matchingTranslation.languageName = extractLanguageName(subjectLanguage.displayBase || subjectLanguage.language);
        matchingTranslation.languageDisplay = buildDisplayName(
          subjectLanguage.displayBase || subjectLanguage.language,
          subjectLanguage.code
        );
      }
    }
    
    // Sort alphabetically by language name (e.g., "Danish", "English", "German")
    translations.sort((a, b) => (a.languageName || '').localeCompare(b.languageName || ''));
    
    return translations;
  }
</script>

<!-- Add loading overlay for export -->
{#if isExporting}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg flex flex-col items-center">
      <div class="loader mb-4"></div>
      <p class="text-sm text-gray-700 dark:text-gray-300">Preparing Excel export...</p>
    </div>
  </div>
{/if}

<StudyHeader />

<FeatureInfo
  title="Diary Entries"
  description="This page displays diary entries submitted by participants. You can filter by start date, date range and form type.
  View detailed diary entry data by clicking on a diary entry, and export data to Excel for further analysis. 
  Diary entries include blood glucose readings, food intake, medications, and other study-specific diary data."
  marginBottom="mb-0"
/>

{#if $selectedParticipant}
  <ParticipantDetails showOptionsMenu={false} />
{:else if $selectedSite}
  <SiteDetails showOptionsMenu={false} />
{:else if $selectedStudy}
  <StudyDetails showOptionsMenu={false} />
{/if}

<!-- Title and Filters -->
<Toolbar title="Diary Entries" itemCountTitle="Diary Entries" itemCount={resultCount}>
  <div class="flex items-baseline gap-2">
    Date:
    <input
      type="date"
      bind:value={selectedDate}
      on:change={handleDateChange}
      class="h-6 w-32 px-2 border rounded dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-100 dark:border-gray-600"
    />
  </div>
  <div class="flex items-baseline gap-2">
    Range:
    <select
      bind:value={dayRange}
      on:change={handleDateChange}
      class="form-select h-6 w-16 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
    >
      <option value={0}>All</option>
      <option value={7}>7</option>
      <option value={14}>14</option>
      <option value={28}>28</option>
    </select>
  </div>
  <div class="flex items-baseline gap-2">
    Type:
    <select
      id="type"
      bind:value={selectedType}
      on:change={() => fetchForms(1)}
      class="form-select h-6 w-52 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
    >
      <option value="ALL">All</option>
      {#each availableFormTypes as formType}
        <option value={formType}>{formType}</option>
      {/each}
    </select>
  </div>
  {#if !$selectedParticipant}
    <div class="flex items-baseline gap-2">
      Status:
      <select
        bind:value={selectedStatus}
        on:change={() => fetchForms(1)}
        class="form-select h-6 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
      >
        <option value="ALL">All</option>
        <option value="COMPD">Completed</option>
        <option value="CONS">Consented</option>
        <option value="DISC">Discontinued</option>
        <option value="INIT">Initiated</option>
        <option value="SCFA">Screen Failed</option>
        <option value="SCRE">Screened</option>
        <option value="ENRO">Screened/Enrolled</option>
      </select>
    </div>
  {/if}
  <div class="flex items-baseline gap-2">
    <button on:click={handleRefresh} class="pagination" title="Refresh">
      <FontAwesomeIcon icon={faRotate} />
    </button>
    {#if $selectedParticipant && canAddForms}
      <button
        on:click={openAddFormModal}
        class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-3 rounded-full text-xs flex items-center gap-1"
        title="Add Form"
      >
        <FontAwesomeIcon icon={faPlus} />
        Add Form
      </button>
    {/if}
  </div>
</Toolbar>

{#if isLoading}
  <div class="flex flex-col justify-center items-center h-64 bg-stone-100/50 dark:bg-gray-725 mx-1">
    <div class="loader mb-4"></div>
    <p class="text-xs justify-center text-gray-700 dark:text-gray-300">preparing forms report</p>
  </div>
{:else}
  <div class="px-1 pt-0">
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          {#if !$selectedSite}
            <th class="px-4 py-2 text-left bold">Site</th>
          {/if}
          {#if !$selectedParticipant}
            <th class="px-4 py-2 text-left bold">Subject</th>
            <th class="px-4 py-2 text-left bold">Status</th>
          {/if}
          <th class="px-4 py-2 text-left bold">Name</th>
          <th class="px-4 py-2 text-left bold w-56">Created (Local)</th>
          <th class="px-4 py-2 text-left bold w-56">Timezone</th>
          <th class="px-4 py-2 text-left bold">Summary</th>
          <th class="px-4 py-2 text-left bold w-56">Form Date (Form TZ)</th>
          <th class="px-4 py-2 text-left bold w-56">Form Timezone</th>
          <th class="px-4 py-2 text-left bold w-12">&nbsp;</th>
        </tr>
      </thead>
      <tbody>
        {#each forms as form, index}
          {@const formData = (() => {
            try {
              if (!form.F.properties) return [];
              const cleanProperties = form.F.properties.replace(/\n/g, '\\n');
              return flattenData(JSON.parse(cleanProperties));
            } catch (e) {
              console.error('Error parsing form properties:', e, form);
              return [];
            }
          })()}
          {@const formDateLocal = getFormDate(form.F.name, formData, form.F.timezone || 'UTC', form.F, formDatePriority)}
          {@const formDateFormTZ = getFormDate(form.F.name, formData, form.F.timezone || 'UTC', form.F, formDatePriority)}
          {@const formDateUTC = getFormDate(form.F.name, formData, 'UTC', form.F, formDatePriority)}
          {@const propertiesDateRaw = extractDateFromProperties(form.F.name, formData)}
          {@const propertiesDateFormatted = propertiesDateRaw ? formatToLocalDateTime(propertiesDateRaw, form.F.timezone || 'UTC') : 'N/A'}
          {@const documentFormDateFormatted = form.F.formDate ? formatToLocalDateTime(form.F.formDate, form.F.timezone || 'UTC') : 'N/A'}
          {@const hoverText = `Properties Date (form tz): ${propertiesDateFormatted}\nDocument formDate (UTC→form tz): ${documentFormDateFormatted}\nPriority: ${formDatePriority}`}
          <tr
            class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
              2 ===
            0
              ? 'bg-stone-50 dark:bg-gray-725'
              : 'bg-stone-75 dark:bg-gray-750'}"
          >
            {#if !$selectedSite}
              <td class="px-4 py-2">{form.SI?.siteId || 'n/a'}</td>
            {/if}
            {#if !$selectedParticipant}
              <td class="px-4 py-2">{form.S?.subjectId || 'n/a'}</td>
              <td class="px-4 py-2">{form.SS?.label || 'n/a'}</td>
            {/if}
            <td class="px-4 py-2">{form.F.name}</td>
            <td class="px-4 py-2 w-56">
              {form.F.received
                ? formatToLocalDateTime(form.F.received, form.TZ?.code || 'UTC')
                : ''}
            </td>
            <td class="px-4 py-2">{form.TZ?.code || 'UTC'}</td>
            <td
              class="px-4 py-2 cursor-pointer"
              on:click={() => {
                const translations = buildFormTranslations(form.FD, form.SL);
                showModal(
                  form.F.name,
                  form.F.properties,
                  form.F.form,
                  {
                    local: formDateLocal,
                    formTz: formDateFormTZ,
                    utc: formDateUTC,
                    formTimezone: form.F.timezone || 'UTC',
                    displayTimezone: form.TZ?.code || 'UTC'
                  },
                  translations,
                  form.SL?._id ? normalizeIdForTranslation(form.SL._id) : null
                );
              }}
            >
              {#if form.F.name.includes('DTSQ')}
                {@const dtsqQuestions = formData.filter((d) =>
                  d.component_name.startsWith('dtsq_question_')
                )}
                Click to view details
              <!-- Details shown in modal -->
              {:else if form.F.name.includes('FSBS')}
                {@const labelsMap = getEnglishLabelsFromFD(form.FD)}
                {@const fsbsReading = formData.find((d) => d.component_name === 'fsbsReading')?.value || '-'}
                {@const fsbsReasons = formData
                  .filter((d) => d.component_name.startsWith('fsbsReason.') && d.value === true)
                  .map((d) => {
                    let label = labelsMap[d.component_name] || d.component_name.split('.')[1]?.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()) || d.component_name;
                    return label.replace(/, specify$/i, '');
                  })}
                {@const isHypo = formData.find((d) => d.component_name === 'fsbsReason.hypoglycemic')?.value}
                {fsbsReading} mg/dL
                {#if fsbsReasons.length}
                  (Reason: {fsbsReasons.join(', ')})
                {/if}
                {#if !fsbsReasons.length && isHypo}
                  (Hypoglycemic)
                {/if}
              {:else if form.F.name.includes('Food')}
                {@const foodItems = formData.filter((d) => d.component_name.includes('foodItems'))}
                {#each [...Array(Math.floor(foodItems.length / 2))].map((_, i) => i) as index, i}
                  {(
                    formData.find((d) => d.component_name === `foodItems[${index}].FoodItem`)
                      ?.value || ''
                  )
                    .trim()
                    .replace(/^\w/, (c) => c.toUpperCase())}:
                  {formData.find((d) => d.component_name === `foodItems[${index}].FoodPercentage`)
                    ?.value}%{#if i < Math.floor(foodItems.length / 2) - 1}, {' '}{/if}
                {/each}
                {#if formData.find((d) => d.component_name === 'restaurant')?.value === 'yes'}
                  @ {formData.find((d) => d.component_name === 'restaurantName')?.value}
                {/if}
              {:else if form.F.name.includes('Starch')}
                {#if formData.find((d) => d.component_name === 'StarchType.glycosade')?.value}
                  Glycosade
                {:else if formData.find((d) => d.component_name === 'StarchType.cornstarch')?.value}
                  Cornstarch
                {:else if formData.find((d) => d.component_name === 'StarchType.tapioca')?.value}
                  Tapioca
                {:else if formData.find((d) => d.component_name === 'StarchType.other')?.value}
                  {formData.find((d) => d.component_name === 'OtherStarch')?.value}
                {/if}
                {formData.find((d) => d.component_name === 'StarchGrams')?.value || '-'}g
              {:else if form.F.name.includes('Ketone')}
                {@const labelsMap = getEnglishLabelsFromFD(form.FD)}
                {@const ketoneValue = formData.find((d) => d.component_name === 'KetoneValue')?.value || '-'}
                {@const reasonLabels = formData
                  .filter((d) => d.component_name.startsWith('Reason.') && d.value === true)
                  .map((d) => {
                    let label = labelsMap[d.component_name] || d.component_name.split('.')[1]?.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()) || d.component_name;
                    return label.replace(/, specify$/i, '');
                  })}
                {ketoneValue} mmol/L{reasonLabels.length ? ` (Reason: ${reasonLabels.join(', ')})` : ''}
              {:else if form.F.name.includes('Blood Glucose')}
                {formData.find((d) => d.component_name === 'fsbsReading')?.value || '-'} mg/dL
              {:else if form.F.name.includes('Hypo')}
                {@const labelsMap = getEnglishLabelsFromFD(form.FD)}
                {@const selectedItems = formData.filter((d) => d.value === true && !d._isArray)}
                {@const otherComments = formData.find((d) => d.component_name === 'OtherComments')?.value || ''}
                {selectedItems.length > 0 
                  ? selectedItems
                      .map((d) => {
                        // For "other" option, show the OtherComments value instead of the label
                        if (d.component_name.endsWith('.other') && otherComments) {
                          return otherComments;
                        }
                        // Get label and remove ", specify" suffix
                        let label = labelsMap[d.component_name] || d.component_name.split('.')[1]?.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()) || d.component_name;
                        return label.replace(/, specify$/i, '');
                      })
                      .join(', ')
                  : 'Click to view details'}
              {:else if form.F.name.includes('Insulin')}
                {@const labelsMap = getEnglishLabelsFromFD(form.FD)}
                {@const units = formData.find((d) => d.component_name === 'Units')?.value || '-'}
                {@const drugName = formData.find((d) => d.component_name === 'nameOfDrug')?.value || ''}
                {@const routeDropdown = formData.find((d) => d.component_name === 'Route' && typeof d.value === 'string')}
                {@const routeEntry = formData.find((d) => d.component_name.startsWith('Route.insulin') && d.value === true)}
                {@const route = (() => {
                  if (routeDropdown && routeDropdown.value) {
                    const code = routeDropdown.value;
                    if (labelsMap[`Route.${code}`]) return labelsMap[`Route.${code}`];
                    if (code.startsWith('insulin')) {
                      return code.replace(/^insulin/, '').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (s) => s.toUpperCase()).trim();
                    }
                    return code;
                  }
                  if (routeEntry) {
                    if (labelsMap[routeEntry.component_name]) return labelsMap[routeEntry.component_name];
                    const key = routeEntry.component_name.replace('Route.', '');
                    if (key.startsWith('insulin')) {
                      return key.replace(/^insulin/, '').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (s) => s.toUpperCase()).trim();
                    }
                  }
                  return '';
                })()}
                {#if drugName || route || units !== '-'}
                  {[units !== '-' ? `${units} units` : '', drugName, route].filter(Boolean).join(', ')}
                {:else}
                  Click to view details
                {/if}
              {:else if form.F.name.includes('Immunosuppression')}
                {formData
                  .filter((d) => d.value === true)
                  .map((d) => d.component_name.split('.')[1])
                  .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
                  .join(', ')}
              {:else}
                Click to view details
              {/if}
            </td>
            <td 
              class="px-4 py-2 w-56"
              title={canEditForms ? hoverText : null}
            >
              {formDateLocal}
            </td>
            <td class="px-4 py-2 w-56">
              {formDateLocal ? (form.F.timezone || 'UTC') : ''}
            </td>
            <td class="px-4 py-2 w-12">
              {#if canEditForms || canDeleteForms}
                <div class="menu-container relative">
                  <button
                    on:click={(e) => handleMenuClick(form.F, e)}
                    class="pagination mx-1 p-0"
                    title="More options"
                  >
                    <FontAwesomeIcon icon={faEllipsis} class="text-sky-800 dark:text-white/70" />
                  </button>
                  {#if showMenu && menuForm?._id === form.F._id}
                    <div
                      bind:this={menuRef}
                      class="fixed w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 menu-dropdown"
                      style="top: {menuPosition.top}px; left: {menuPosition.left}px; max-height: 200px; overflow-y: auto;"
                    >
                      {#if canEditForms}
                        <button
                          on:click|stopPropagation={() => {
                            handleEditForm(form.F);
                            handleMenuClose();
                          }}
                          class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                          title="Edit form"
                        >
                          <span>Edit form</span>
                          <FontAwesomeIcon
                            icon={faPencil}
                            class="text-sky-800 dark:text-white/70"
                          />
                        </button>
                      {/if}
                      {#if canViewAuditLogs}
                        <button
                          on:click|stopPropagation={() => {
                            handleAuditLogClick(form.F);
                            handleMenuClose();
                          }}
                          class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                          disabled={!canViewAuditLogs}
                          class:opacity-50={!canViewAuditLogs}
                          title={!canViewAuditLogs ? 'Insufficient permissions' : 'View audit logs'}
                        >
                          <span>View audit logs</span>
                          <FontAwesomeIcon
                            icon={faHistory}
                            class="text-sky-800 dark:text-white/70"
                          />
                        </button>
                      {/if}
                      {#if canViewEditDebug}
                        <button
                          on:click|stopPropagation={() => {
                            showEditDebugFromMenu(form.F);
                            handleMenuClose();
                          }}
                          class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                          title="View debug JSON"
                        >
                          <span>View debug JSON</span>
                          <FontAwesomeIcon
                            icon={faBug}
                            class="text-sky-800 dark:text-white/70"
                          />
                        </button>
                      {/if}
                      {#if canDeleteForms}
                        <button
                          on:click|stopPropagation={() => {
                            showDeleteConfirmationModal(form.F);
                            handleMenuClose();
                          }}
                          class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                          title="Delete form"
                        >
                          <span>Delete form</span>
                          <FontAwesomeIcon
                            icon={faTrashCan}
                            class="text-sky-800 dark:text-white/70"
                          />
                        </button>
                      {/if}
                    </div>
                  {/if}
                </div>
              {/if}
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<!-- Add Control Buttons -->
<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
  <div>
    <button
      on:click={handleExportExcel}
      class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-4 rounded-full text-xs"
      >Excel</button
    >
  </div>
</Footer>

<Modal {isModalOpen} {modalTitle} {modalData} formDefinition={modalFormDefinition} dates={modalDates} formTranslations={modalFormTranslations} participantLanguageGUID={modalParticipantLanguageGUID} on:close={closeModal} />

{#if showEditDebugModal}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[80vh] overflow-y-auto">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Edit Form Debug Info</h2>
        <button
          on:click={closeEditDebugModal}
          class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          title="Close"
        >
          <FontAwesomeIcon icon={faTimes} class="w-5 h-5" />
        </button>
      </div>
      <p class="text-xs text-gray-600 dark:text-gray-400 mb-4">
        This data is read-only and can help identify malformed JSON in the form definition or properties.
      </p>
      <div class="space-y-4">
        <div>
          <div class="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Context</div>
          <textarea
            class="w-full h-28 px-3 py-2 text-xs font-mono border rounded bg-gray-50 dark:bg-gray-900 border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-200"
            readonly
          >{editDebugPayload}</textarea>
        </div>
        <div>
          <div class="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Form JSON</div>
          <textarea
            class="w-full h-48 px-3 py-2 text-xs font-mono border rounded bg-gray-50 dark:bg-gray-900 border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-200"
            readonly
          >{editDebugFormJSON}</textarea>
        </div>
        <div>
          <div class="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Properties JSON</div>
          <textarea
            class="w-full h-48 px-3 py-2 text-xs font-mono border rounded bg-gray-50 dark:bg-gray-900 border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-200"
            readonly
          >{editDebugPropertiesJSON}</textarea>
        </div>
      </div>
    </div>
  </div>
{/if}

<!-- Add Form Modal -->
{#if showAddFormModal}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div
      class="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto"
    >
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Add Form Activity</h2>
        <button
          on:click={closeAddFormModal}
          class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <FontAwesomeIcon icon={faTimes} class="w-5 h-5" />
        </button>
      </div>

      {#if isLoadingFormActivities}
        <div class="flex flex-col justify-center items-center h-32">
          <div class="loader mb-4"></div>
          <p class="text-sm text-gray-700 dark:text-gray-300">Loading form activities...</p>
        </div>
      {:else if formActivities.length === 0}
        <div class="text-center py-8">
          <p class="text-gray-500 dark:text-gray-400">
            No form activities found in the published schedule.
          </p>
        </div>
      {:else}
        <div class="space-y-2">
          <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Select a form activity from the published schedule to add to this participant's forms:
          </p>
          {#each formActivities as activity}
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <!-- svelte-ignore a11y-no-static-element-interactions -->
            <div
              class="border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
              on:click={() => handleAddForm(activity)}
            >
              <div class="flex items-center justify-between">
                <div class="flex-1">
                  <h3 class="font-medium text-gray-900 dark:text-white">
                    {activity._isFromGroup ? activity.formName : activity.name}
                  </h3>
                  {#if activity.description}
                    <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {activity.description}
                    </p>
                  {/if}
                  <div
                    class="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400"
                  >
                    {#if activity.formName}
                      <span>Form: {activity.formName}</span>
                    {/if}
                    {#if activity.code}
                      <span>Code: {activity.code}</span>
                    {/if}
                    {#if activity.onDemand}
                      <span class="text-blue-600 dark:text-blue-400">On Demand</span>
                    {/if}
                  </div>
                </div>
                <FontAwesomeIcon icon={faEye} class="text-sky-800 dark:text-white/70 ml-4" />
              </div>
            </div>
          {/each}
        </div>
      {/if}

      <div class="flex justify-end mt-6">
        <button
          on:click={closeAddFormModal}
          class="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- Form Filling Modal -->
{#if showFormFillingModal}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div
      class="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"
    >
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
          {isEditing ? 'Edit ' : ''}{selectedFormDefinition?.name || 'Form'}
        </h2>
        <div class="flex items-center gap-2">
          {#if formTranslationsForFilling.length > 1 && !isEditing}
            {@const participantLangGUID = normalizeGUID($selectedParticipant?.languageGUID)}
            <select
              value={selectedFillingLanguageGUID}
              on:change={handleFillingLanguageChange}
              class="px-2 py-1 min-w-[13.5rem] text-xs border rounded bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
            >
              {#each formTranslationsForFilling as translation}
                {@const isParticipantLang = participantLangGUID && translation.languageGUID === participantLangGUID}
                <option value={translation.languageGUID}>
                  {translation.languageDisplay || translation.languageCode || 'Unknown'}{isParticipantLang ? ' (Participant)' : ''}
                </option>
              {/each}
            </select>
          {/if}
          <button
            on:click={closeFormFillingModal}
            class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <FontAwesomeIcon icon={faTimes} class="w-5 h-5" />
          </button>
        </div>
      </div>

      {#if isLoadingFormDefinition}
        <div class="flex flex-col justify-center items-center h-32">
          <div class="loader mb-4"></div>
          <p class="text-sm text-gray-700 dark:text-gray-300">Loading form definition...</p>
        </div>
      {:else if selectedFormDefinition}
        <div class="space-y-4">
          {#if selectedFormJSON}
            {@const formConfig = (() => {
              try {
                return parseFormConfig(selectedFormJSON);
              } catch (e) {
                console.error('Error parsing form config:', e);
                return null;
              }
            })()}

            {#if formConfig}
              {#if !isFormTypeDetermined}
                <!-- Loading state while determining form type -->
                <div class="flex items-center justify-center py-8">
                  <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span class="ml-3 text-gray-600 dark:text-gray-400">Loading form...</span>
                </div>
              {:else if isMultiSectionForm && formSections.length > 0}
                {@const questionnaireInfo = (() => {
                  try {
                    const formConfig = parseFormConfig(selectedFormJSON) || {};
                    return {
                      name: formConfig.name || null,
                      description: formConfig.description || null,
                      copyright: formConfig.copyright || null,
                    };
                  } catch (e) {
                    console.error('Error parsing form config for questionnaire info:', e);
                    return null;
                  }
                })()}
                <MultiSectionForm
                  {formSections}
                  {currentSectionIndex}
                  {formData}
                  {formErrors}
                  {isEditing}
                  {questionnaireInfo}
                  on:sectionChange={(e) => (currentSectionIndex = e.detail.index)}
                  on:cancel={closeFormFillingModal}
                  on:submit={handleSubmitForm}
                />
              {:else}
                {@const formConfig = (() => {
                  try {
                    return parseFormConfig(selectedFormJSON || selectedFormDefinition.form) || {};
                  } catch (e) {
                    console.error('Error parsing form config:', e);
                    return {};
                  }
                })()}
                <RegularForm
                  {formConfig}
                  {formData}
                  {formErrors}
                  {addableFieldGroups}
                  {isEditing}
                  on:cancel={closeFormFillingModal}
                  on:submit={handleSubmitForm}
                />
              {/if}
            {:else}
              <p class="text-gray-500 dark:text-gray-400">Error loading form configuration.</p>
            {/if}
          {:else}
            <p class="text-gray-500 dark:text-gray-400">No form definition available.</p>
          {/if}
        </div>
      {/if}
    </div>
  </div>
{/if}

<!-- Delete Confirmation Modal -->
{#if showDeleteConfirmation}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Delete Form</h2>
        <button
          on:click={closeDeleteConfirmationModal}
          class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <FontAwesomeIcon icon={faTimes} class="w-5 h-5" />
        </button>
      </div>

      <p class="text-gray-700 dark:text-gray-300 mb-6">
        Are you sure you want to delete "{formToDelete?.name}"? This action cannot be undone.
      </p>

      <div class="flex justify-end gap-3">
        <button
          on:click={closeDeleteConfirmationModal}
          class="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
        <button
          on:click={handleDeleteForm}
          class="bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition-colors"
        >
          Delete
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- Audit Log Modal -->
{#if showAuditLog}
  <AuditLogModal
    showModal={showAuditLog}
    documentId={auditLogForm?._id}
    collection="SubjectForm"
    title="Diary Entry Audit Logs"
    on:close={() => {
      showAuditLog = false;
      auditLogForm = null;
    }}
  />
{/if}

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  /* Form-specific styles moved to components */

  .fixed {
    position: fixed;
  }

  .inset-0 {
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  .bg-opacity-50 {
    --tw-bg-opacity: 0.5;
  }

  .z-50 {
    z-index: 50;
  }

  .shadow-lg {
    --tw-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
    --tw-shadow-colored: 0 10px 15px -3px var(--tw-shadow-color),
      0 4px 6px -4px var(--tw-shadow-color);
    box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000),
      var(--tw-shadow);
  }

  /* Form-specific styles moved to components */
</style>
